import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../lib/api";
import { MessageCircleQuestion, Send, Sparkle } from "lucide-react";

const STARTERS = [
  "What documents does PTCC need?",
  "What if my line crosses a 132 kV line?",
  "Which approvals can run in parallel?",
  "What usually delays Section 164?",
  "Which approvals must be complete before charging?",
];

const OFFLINE_MSG = "Expert Q&A is temporarily offline — book a Solarian consult and an expert will answer your question directly.";

function Msg({ m }) {
  const beyond = m.beyond_kb || m.content?.trimStart().startsWith("[BEYOND_KB]");
  const text = (m.content || "").replace("[BEYOND_KB]", "").trim();
  if (m.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] bg-[#0B1F3A] text-white rounded-2xl rounded-br-sm px-4 py-2.5 text-sm whitespace-pre-wrap" data-testid="chat-msg-user">{text}</div>
      </div>
    );
  }
  return (
    <div className="flex justify-start">
      <div className="max-w-[85%]">
        {beyond && (
          <span className="inline-block mb-1 text-[10px] font-semibold uppercase tracking-wider bg-violet-100 text-violet-700 border border-violet-300 rounded-full px-2 py-0.5" data-testid="chat-beyond-kb-chip">
            AI guidance — expert verification recommended
          </span>
        )}
        <div className="bg-white border border-[#0B1F3A]/15 rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm text-[#0B1F3A] whitespace-pre-wrap" data-testid="chat-msg-assistant">
          {text}{m.streaming && <span className="inline-block w-1.5 h-4 bg-[#1D4ED8] ml-0.5 animate-pulse align-middle" />}
        </div>
      </div>
    </div>
  );
}

function GateCard() {
  const navigate = useNavigate();
  const location = useLocation();
  return (
    <div className="bg-white border-2 border-[#1D4ED8] rounded-xl px-4 py-4 text-center" data-testid="chat-signup-card">
      <Sparkle size={18} className="inline text-[#1D4ED8]" />
      <p className="font-serif font-semibold text-[#0B1F3A] mt-1">Create a free account to keep asking</p>
      <p className="text-xs text-[#475569] mt-1">Your conversation will be saved — you'll continue right here.</p>
      <div className="flex justify-center gap-2 mt-3">
        <button onClick={() => navigate(`/signup?next=${encodeURIComponent(location.pathname)}`)} className="bg-[#1D4ED8] text-white rounded-md px-4 py-2 text-xs font-semibold" data-testid="chat-gate-signup-btn">Create free account</button>
        <button onClick={() => navigate(`/login?next=${encodeURIComponent(location.pathname)}`)} className="border border-[#0B1F3A]/25 text-[#0B1F3A] rounded-md px-4 py-2 text-xs font-medium" data-testid="chat-gate-login-btn">Log in</button>
      </div>
    </div>
  );
}

// Reads the SSE body and invokes onEvent for each parsed `data:` payload
async function readSseStream(res, onEvent) {
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      if (p.startsWith("data: ")) onEvent(JSON.parse(p.slice(6)));
    }
  }
}

function useChat(contextId) {
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [gate, setGate] = useState(false);
  const keyRef = useRef(0);
  const nextKey = () => `m${keyRef.current++}`;

  useEffect(() => {
    api.get(`/chat/${contextId}`)
      .then(({ data }) => setMessages(data.map((m, idx) => ({ ...m, _k: `h${idx}` }))))
      .catch((e) => console.debug("chat: no saved history", e));
  }, [contextId]);

  const updateLast = (fn) => setMessages((m) => {
    const copy = [...m];
    copy[copy.length - 1] = fn({ ...copy[copy.length - 1] });
    return copy;
  });

  const send = async (message) => {
    if (!message || busy) return;
    setBusy(true);
    setMessages((m) => [...m,
      { role: "user", content: message, _k: nextKey() },
      { role: "assistant", content: "", streaming: true, _k: nextKey() }]);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/${contextId}/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message }),
      });
      if (res.status === 403) {
        setMessages((m) => m.slice(0, -2));
        setGate(true);
        return;
      }
      if (!res.ok) throw new Error(`chat failed (${res.status})`);
      await readSseStream(res, (d) => updateLast((last) => {
        if (d.t) last.content = (last.content || "") + d.t;
        if (d.done) {
          last.streaming = false;
          last.beyond_kb = !!d.beyond_kb;
        }
        return last;
      }));
    } catch (e) {
      console.error("chat: stream failed", e);
      updateLast((last) => ({ ...last, content: OFFLINE_MSG, streaming: false }));
    } finally {
      setBusy(false);
    }
  };

  return { messages, busy, gate, send };
}

export default function ChatPanel({ contextId }) {
  const { messages, busy, gate, send } = useChat(contextId);
  const [input, setInput] = useState("");
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages, gate]);

  const submit = (text) => {
    const message = (text || input).trim();
    if (!message) return;
    setInput("");
    send(message);
  };

  return (
    <div className="bg-[#F5F8FF] border border-[#0B1F3A]/15 rounded-xl overflow-hidden" data-testid="chat-panel">
      <div className="bg-white border-b border-[#0B1F3A]/10 px-4 sm:px-5 py-3.5 flex items-center gap-2.5">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50 text-[#1D4ED8]"><MessageCircleQuestion size={17} /></span>
        <div>
          <p className="font-serif font-semibold text-[#0B1F3A]">Ask the AI about this roadmap</p>
          <p className="text-[11px] text-[#475569]">Answers cite Solarian's expert-verified knowledge base.</p>
        </div>
      </div>

      <div className="px-4 sm:px-5 py-4 space-y-3 max-h-[420px] overflow-y-auto">
        {messages.length === 0 && (
          <p className="text-xs text-[#475569] text-center py-2">Start with one of these, or ask anything about your approvals:</p>
        )}
        {messages.map((m, i) => <Msg key={m._k ?? i} m={m} />)}
        {gate && <GateCard />}
        <div ref={bottomRef} />
      </div>

      {messages.length === 0 && !gate && (
        <div className="px-4 sm:px-5 pb-2 flex flex-wrap gap-1.5">
          {STARTERS.map((s) => (
            <button key={s} onClick={() => submit(s)} disabled={busy}
              className="text-[11px] border border-[#1D4ED8]/30 text-[#1D4ED8] bg-white rounded-full px-3 py-1.5 hover:bg-blue-50 disabled:opacity-50 transition-colors" data-testid="chat-starter-chip">
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="px-4 sm:px-5 py-3 bg-white border-t border-[#0B1F3A]/10">
        <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="flex gap-2">
          <input value={input} onChange={(e) => setInput(e.target.value)} disabled={busy || gate}
            placeholder={gate ? "Create a free account to continue…" : "Ask about documents, timelines, sequence…"}
            className="flex-1 border border-[#0B1F3A]/20 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#1D4ED8] focus:outline-none disabled:bg-[#F5F8FF]" data-testid="chat-input" />
          <button type="submit" disabled={busy || gate || !input.trim()}
            className="bg-[#1D4ED8] text-white rounded-md px-4 py-2.5 disabled:opacity-50" data-testid="chat-send-btn" aria-label="Send">
            <Send size={16} />
          </button>
        </form>
      </div>
    </div>
  );
}
